import { useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ManagerLayout from "../../layouts/ManagerLayout";
import * as XLSX from "xlsx";
import { createProjectDiscipline } from "../../api/projectService";

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "bg-white/10",     text: "text-white/50",   label: "Pending"   },
    success: { bg: "bg-green-500/20", text: "text-green-300",  label: "✓ Created"  },
    error:   { bg: "bg-red-500/20",   text: "text-red-300",    label: "✗ Failed"   },
    skipped: { bg: "bg-yellow-500/20",text: "text-yellow-300", label: "— Skipped"  },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

export default function BulkCreateDisciplines() {
  const { projectId } = useParams();
  const navigate      = useNavigate();
  const fileInputRef  = useRef(null);

  const [rows, setRows]             = useState([]);
  const [parseError, setParseError] = useState("");
  const [uploading, setUploading]   = useState(false);
  const [results, setResults]       = useState([]);
  const [done, setDone]             = useState(false);

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const headers = projectId ? ["discipline_id", "estimated_hours"] : ["discipline_id", "project_id", "estimated_hours"];
    const examples = projectId
      ? [
          ["DESIGN", 100],
          ["DEVELOPMENT", 200],
          ["QA-TESTING", 50],
          ["DEPLOYMENT", 20],
          ["REVIEW", 10]
        ]
      : [
          ["DESIGN",      "PROJ-001", 100],
          ["DEVELOPMENT", "PROJ-001", 200],
          ["QA-TESTING",  "PROJ-001", 50],
          ["DESIGN",      "PROJ-002", 120],
          ["BACKEND",     "PROJ-002", 150],
        ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...examples]);
    ws["!cols"] = projectId
      ? [{ wch: 24 }, { wch: 18 }]
      : [{ wch: 24 }, { wch: 20 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, ws, "disciplines_bulk");
    XLSX.writeFile(wb, "bulk_disciplines_template.xlsx");
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError(""); setRows([]); setResults([]); setDone(false);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: "" });

        if (!raw.length) { setParseError("File is empty or unreadable."); return; }

        const needsProjectId = !projectId;
        if (!("discipline_id" in raw[0])) { setParseError("Missing required column: discipline_id"); return; }
        if (needsProjectId && !("project_id" in raw[0])) { setParseError("Missing required column: project_id (required when not scoped to a project)"); return; }

        const seen = new Set();
        const parsed = raw
          .map((r, i) => {
            const sid = String(r.discipline_id || "").trim().toUpperCase();
            const pid = projectId || String(r.project_id || "").trim().toUpperCase();
            
            // Handle estimated_hours variants
            const rawHours = r.estimated_hours !== undefined ? r.estimated_hours 
                           : (r["estimated hours"] !== undefined ? r["estimated hours"] 
                           : (r["Estimated Hours"] !== undefined ? r["Estimated Hours"] : 0.0));
            const eh = parseFloat(rawHours) || 0.0;

            const key = `${pid}:${sid}`;
            let rowError = "";
            if (!sid) rowError = "discipline_id is required";
            else if (needsProjectId && !pid) rowError = "project_id is required";
            else if (seen.has(key)) rowError = "Duplicate discipline_id for this project in file";
            else seen.add(key);
            
            return { 
              _idx: i + 1, 
              discipline_id: sid, 
              project_id: pid, 
              estimated_hours: eh,
              _error: rowError, 
              _status: rowError ? "skipped" : "pending" 
            };
          })
          .filter((r) => r.discipline_id || r.project_id);

        if (!parsed.length) { setParseError("No data rows found."); return; }
        setRows(parsed);
      } catch {
        setParseError("Could not parse file. Please use the provided template.");
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  const handleUpload = async () => {
    const toProcess = rows.filter((r) => r._status === "pending");
    if (!toProcess.length) return;
    setUploading(true);

    const live = rows.map((r) => ({ ...r }));

    for (let i = 0; i < live.length; i++) {
      const row = live[i];
      if (row._status !== "pending") continue;

      try {
        await createProjectDiscipline(row.project_id, { 
          discipline_id: row.discipline_id, 
          estimated_hours: row.estimated_hours 
        });
        live[i] = { ...row, _status: "success", _error: "" };
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Unknown error";
        live[i] = { ...row, _status: "error", _error: msg };
      }

      setResults([...live]);
    }

    setUploading(false);
    setDone(true);
  };

  const displayRows  = results.length ? results : rows;
  const pendingCount = rows.filter((r) => r._status === "pending").length;
  const successCount = results.filter((r) => r._status === "success").length;
  const errorCount   = results.filter((r) => r._status === "error").length;
  const skippedCount = results.filter((r) => r._status === "skipped").length;

  return (
    <ManagerLayout>
      <div className="p-3 sm:p-6">
        <div className="w-full max-w-[1400px] mx-auto">

          {/* HEADER */}
          <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 max-w-5xl mx-auto">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-2xl sm:text-3xl">📊</span>
                </div>
                <div>
                  <h1 className="text-xl sm:text-3xl font-bold text-white">Bulk Create Disciplines</h1>
                  <p className="text-white/60 text-xs sm:text-sm">
                    {projectId
                      ? `Upload Excel to create multiple disciplines for Project ${projectId}`
                      : "Upload Excel to create disciplines across multiple projects"}
                  </p>
                </div>
              </div>
              <Link to={`/manager/projects/${projectId}/create-discipline`} className="w-full sm:w-auto">
                <button className="w-full sm:w-auto px-5 py-2.5 rounded-xl border-2 border-white/30 text-white/80 hover:bg-white/10 text-sm font-medium transition-all">
                  ← Single Create
                </button>
              </Link>
            </div>
          </motion.div>

          {/* Info banner */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05 }}
            className="max-w-5xl mx-auto mb-4 sm:mb-5 bg-cyan-500/10 border border-cyan-400/25 rounded-xl px-4 sm:px-5 py-3 flex items-start gap-3">
            <span className="text-cyan-300 text-lg mt-0.5 flex-shrink-0">ℹ</span>
            <div>
              <p className="text-cyan-200 font-semibold text-sm">Disciplines are created without employee assignments</p>
              <p className="text-white/50 text-xs mt-0.5">After bulk upload, go to each discipline's detail page to assign employees.</p>
            </div>
          </motion.div>

          <div className="max-w-5xl mx-auto space-y-4 sm:space-y-6">

            {/* STEP 1 – Template */}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-6 h-6 rounded-full bg-cyan-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                    <h2 className="text-white font-bold text-base">Download Template</h2>
                  </div>
                  <p className="text-white/50 text-xs sm:text-sm ml-8">
                    Get the Excel template with required columns.
                    {projectId ? " Template only needs discipline_id since project is pre-selected." : " Includes project_id column to create disciplines across projects."}
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
                  onClick={handleDownloadTemplate}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold text-sm shadow-lg w-full sm:w-auto flex-shrink-0"
                >
                  ⬇ Download Template
                </motion.button>
              </div>

              <div className="mt-4 ml-0 sm:ml-8 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 max-w-2xl">
                <div className="bg-white/5 rounded-xl p-2.5 sm:p-3 border border-white/10">
                  <div className="flex items-center gap-1.5 mb-1">
                    <code className="text-cyan-300 text-xs font-bold">discipline_id</code>
                    <span className="text-red-400 text-xs">★</span>
                  </div>
                  <p className="text-white/40 text-xs">Unique within project · stored UPPERCASE · e.g. DESIGN, QA-TESTING</p>
                </div>
                {!projectId && (
                  <div className="bg-white/5 rounded-xl p-2.5 sm:p-3 border border-white/10">
                    <div className="flex items-center gap-1.5 mb-1">
                      <code className="text-cyan-300 text-xs font-bold">project_id</code>
                      <span className="text-red-400 text-xs">★</span>
                    </div>
                    <p className="text-white/40 text-xs">Must match an existing project in the database</p>
                  </div>
                )}
                <div className="bg-white/5 rounded-xl p-2.5 sm:p-3 border border-white/10">
                  <div className="flex items-center gap-1.5 mb-1">
                    <code className="text-cyan-300 text-xs font-bold">estimated_hours</code>
                  </div>
                  <p className="text-white/40 text-xs">Numeric estimate of total discipline hours (optional, defaults to 0)</p>
                </div>
              </div>
            </motion.div>

            {/* STEP 2 – Upload */}
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.15 }}
              className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-6 h-6 rounded-full bg-cyan-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                <h2 className="text-white font-bold text-base">Upload & Preview</h2>
              </div>

              <label
                className="border-2 border-dashed border-white/25 hover:border-cyan-400/60 rounded-xl p-6 sm:p-8 text-center cursor-pointer transition-all hover:bg-cyan-500/5 block"
              >
                <div className="text-4xl mb-2">📂</div>
                <p className="text-white/70 font-medium text-sm">Click to choose file</p>
                <p className="text-white/40 text-xs mt-1">.xlsx or .csv · max 500 rows</p>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} />
              </label>

              {parseError && (
                <div className="mt-4 bg-red-500/15 border border-red-400/30 rounded-xl px-4 py-3 text-red-300 text-sm">
                  ⚠ {parseError}
                </div>
              )}

              <AnimatePresence>
                {displayRows.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-5">

                    {done && (
                      <div className="flex gap-3 mb-4 flex-wrap">
                        <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-300 text-xs font-bold">{successCount} created</span>
                        {errorCount   > 0 && <span className="px-3 py-1 rounded-full bg-red-500/20 text-red-300 text-xs font-bold">{errorCount} failed</span>}
                        {skippedCount > 0 && <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-300 text-xs font-bold">{skippedCount} skipped</span>}
                      </div>
                    )}

                    {/* Desktop table */}
                    <div className="overflow-x-auto rounded-xl border border-white/15 hidden sm:block">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-white/10 text-white/70 text-xs">
                            <th className="px-3 py-2 text-left font-semibold">#</th>
                            <th className="px-3 py-2 text-left font-semibold">discipline_id</th>
                            <th className="px-3 py-2 text-left font-semibold">project_id</th>
                            <th className="px-3 py-2 text-left font-semibold">Estimated Hours</th>
                            <th className="px-3 py-2 text-left font-semibold">Status</th>
                            <th className="px-3 py-2 text-left font-semibold">Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayRows.map((row, i) => (
                            <tr key={i} className={`border-t border-white/10 ${row._status === "error" ? "bg-red-500/5" : row._status === "success" ? "bg-green-500/5" : ""}`}>
                              <td className="px-3 py-2 text-white/40 text-xs">{row._idx}</td>
                              <td className="px-3 py-2 text-white font-mono text-xs">{row.discipline_id || <span className="text-red-400">—</span>}</td>
                              <td className="px-3 py-2 text-white/70 font-mono text-xs">{row.project_id || <span className="text-red-400">—</span>}</td>
                              <td className="px-3 py-2 text-white/70 text-xs">{row.estimated_hours !== undefined ? `${row.estimated_hours} hrs` : "0.0 hrs"}</td>
                              <td className="px-3 py-2"><StatusBadge status={row._status} /></td>
                              <td className="px-3 py-2 text-red-300 text-xs">{row._error || ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile cards */}
                    <div className="sm:hidden space-y-2">
                      {displayRows.map((row, i) => (
                        <div key={i} className={`rounded-xl border p-3 text-xs ${
                          row._status === "error"   ? "bg-red-500/5 border-red-400/20"   :
                          row._status === "success" ? "bg-green-500/5 border-green-400/20" :
                          "bg-white/5 border-white/10"
                        }`}>
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="text-white font-mono font-semibold">{row.discipline_id || "—"}</p>
                              <p className="text-white/50">{row.project_id || "—"}</p>
                              <p className="text-white/40 mt-0.5">Est: {row.estimated_hours !== undefined ? `${row.estimated_hours} hrs` : "0.0 hrs"}</p>
                            </div>
                            <StatusBadge status={row._status} />
                          </div>
                          {row._error && <p className="text-red-300 mt-1">{row._error}</p>}
                        </div>
                      ))}
                    </div>

                    <p className="text-white/40 text-xs mt-2">{displayRows.length} row{displayRows.length !== 1 ? "s" : ""} detected · {pendingCount} ready to upload</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* STEP 3 – Confirm */}
            {rows.length > 0 && !done && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-4 sm:p-5">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-cyan-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">3</span>
                    <h2 className="text-white font-bold text-base">Confirm Upload</h2>
                  </div>
                  <p className="text-white/50 text-xs sm:text-sm ml-8 mt-0.5">
                    {pendingCount} discipline{pendingCount !== 1 ? "s" : ""} will be created (no employee assignment).
                    {rows.length - pendingCount > 0 && ` ${rows.length - pendingCount} skipped.`}
                  </p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
                  onClick={handleUpload}
                  disabled={uploading || pendingCount === 0}
                  className="flex items-center justify-center gap-2 px-7 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold shadow-lg disabled:opacity-50 text-sm w-full sm:w-auto"
                >
                  {uploading ? (
                    <><span className="animate-spin inline-block">⟳</span> Uploading...</>
                  ) : (
                    <>🚀 Upload {pendingCount} Discipline{pendingCount !== 1 ? "s" : ""}</>
                  )}
                </motion.button>
              </motion.div>
            )}

            {/* Done */}
            {done && (
              <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-green-500/10 border border-green-400/30 rounded-2xl p-4 sm:p-5">
                <div>
                  <p className="text-green-300 font-bold text-base">Upload Complete!</p>
                  <p className="text-white/50 text-sm mt-0.5">{successCount} created · {errorCount} failed · {skippedCount} skipped</p>
                  <p className="text-cyan-300/70 text-xs mt-1">👉 Go to Discipline Details to assign employees to each discipline.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={() => { setRows([]); setResults([]); setDone(false); setParseError(""); }}
                    className="px-5 py-2.5 rounded-xl border-2 border-white/30 text-white text-sm hover:bg-white/10 transition-all">
                    Upload Another
                  </button>
                  <button
                    onClick={() => navigate(projectId ? `/manager/discipline-details/${projectId}` : "/manager/projects")}
                    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold text-sm">
                    {projectId ? "View Disciplines →" : "View Projects →"}
                  </button>
                </div>
              </motion.div>
            )}

          </div>
        </div>
      </div>
    </ManagerLayout>
  );
}
